import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold,
  Italic,
  List,
  Link as LinkIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "react-feather";
import { useEffect } from "react";
import {
  checkRichTextLength,
  type RichTextLimitVariant,
} from "@/utils/richText";

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  editorRef?: (editor: Editor | null) => void;
  maxLength?: number;
  limitVariant?: RichTextLimitVariant;
}

const editorStyles = `
  .ProseMirror {
    min-height: 200px;
    padding: 16px;
    border-radius: 0 0 8px 8px;
    outline: none;
    background: transparent;
    color: hsl(var(--foreground));
  }

  .ProseMirror p.is-editor-empty:first-child::before {
    color: hsl(var(--muted-foreground));
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
  }

  .ProseMirror p {
    margin: 1em 0;
  }

  .ProseMirror > *:first-child {
    margin-top: 0;
  }

  .ProseMirror > *:last-child {
    margin-bottom: 0;
  }

  .ProseMirror:focus {
    outline: none;
  }

  .ProseMirror ul,
  .ProseMirror ol {
    padding-left: 2em;
    margin: 1em 0;
  }

  .ProseMirror li {
    margin: 0.5em 0;
  }
`;

export function RichTextEditor({
  content,
  onChange,
  placeholder = "Write your email content here...",
  editorRef,
  maxLength,
  limitVariant = "default",
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-brand-600 underline",
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ["paragraph", "heading"],
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  useEffect(() => {
    if (editorRef) {
      editorRef(editor);
    }
  }, [editor, editorRef]);

  const toggleLink = () => {
    if (!editor) return;

    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl);

    if (url === null) {
      return;
    }

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  if (!editor) {
    return null;
  }

  const { plainLength, htmlLength, isOver } =
    maxLength !== undefined
      ? checkRichTextLength(content, maxLength)
      : { plainLength: 0, htmlLength: 0, isOver: false };

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <style>{editorStyles}</style>
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-muted">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-1 rounded hover:bg-accent ${
            editor.isActive("bold") ? "bg-accent" : ""
          }`}
          title="Bold"
        >
          <Bold size={16} />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-1 rounded hover:bg-accent ${
            editor.isActive("italic") ? "bg-accent" : ""
          }`}
          title="Italic"
        >
          <Italic size={16} />
        </button>
        <span className="w-px h-4 bg-border" />
        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-1 rounded hover:bg-accent ${
            editor.isActive("bulletList") ? "bg-accent" : ""
          }`}
          title="Bullet List"
        >
          <List size={16} />
        </button>
        <span className="w-px h-4 bg-border" />
        <button
          onClick={toggleLink}
          className={`p-1 rounded hover:bg-accent ${
            editor.isActive("link") ? "bg-accent" : ""
          }`}
          title="Add Link"
        >
          <LinkIcon size={16} />
        </button>
        <span className="w-px h-4 bg-border" />
        <button
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          className={`p-1 rounded hover:bg-accent ${
            editor.isActive({ textAlign: "left" }) ? "bg-accent" : ""
          }`}
          title="Align Left"
        >
          <AlignLeft size={16} />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          className={`p-1 rounded hover:bg-accent ${
            editor.isActive({ textAlign: "center" }) ? "bg-accent" : ""
          }`}
          title="Align Center"
        >
          <AlignCenter size={16} />
        </button>
        <button
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          className={`p-1 rounded hover:bg-accent ${
            editor.isActive({ textAlign: "right" }) ? "bg-accent" : ""
          }`}
          title="Align Right"
        >
          <AlignRight size={16} />
        </button>
      </div>
      <div className="bg-transparent">
        <EditorContent
          editor={editor}
          className="prose max-w-none focus:outline-none text-foreground"
        />
      </div>
      {maxLength !== undefined && (
        <div className="flex items-center justify-between gap-3 px-3 py-1.5 bg-transparent border-t border-border">
          <span className="text-xs text-red-500 truncate">
            {isOver
              ? limitVariant === "email"
                ? `As an email template: ${htmlLength} / ${maxLength}`
                : `With formatting: ${htmlLength} / ${maxLength}`
              : ""}
          </span>
          <span
            className={`text-xs tabular-nums shrink-0 ${
              isOver ? "text-red-500" : "text-muted-foreground"
            }`}
          >
            {plainLength} / {maxLength}
          </span>
        </div>
      )}
    </div>
  );
}
